/**
 * Pure filtering and sorting utilities for the Feature Collection Search view.
 *
 * Extracted from Search.jsx so the filtering logic can be tested in isolation.
 */

import { isCollectionCompatibleWithVehicle } from './collectionCompatibility'
import { ACTIVE } from 'utils/constants'

/**
 * Filter and sort a list of feature collections based on the given criteria.
 *
 * @param {object[]} collections - Full list of collection objects.
 * @param {string}   query       - Free-text search query (matches partNumber, name, author, description).
 * @param {Function} comparator  - Sort comparator function (a, b) => number.
 * @param {boolean}  filterCompatible - When true, retain only Active + vehicle-compatible collections.
 * @param {object|null} vehicleContext - Vehicle object used for compatibility checks.
 * @returns {object[]} Filtered and sorted collection list.
 */
export const getFilteredCollectionsList = (
  collections,
  query = '',
  comparator,
  filterCompatible = false,
  vehicleContext = null
) => {
  if (!collections || collections.length === 0) {
    return []
  }

  const filteredList = collections.filter((item) => {
    const { partNumber, name, author, description } = item
    const matches =
      query && query.length >= 1
        ? [partNumber, name, author, description].filter((property) => {
            const words = `${query}`.split(' ').filter((word) => Boolean(word))
            return words.some(
              (word) =>
                `${property}`.toUpperCase().indexOf(`${word}`.toUpperCase()) >
                -1
            )
          }).length > 0
        : true

    if (!matches) return false

    if (filterCompatible && vehicleContext) {
      const { compatible } = isCollectionCompatibleWithVehicle(
        item,
        vehicleContext
      )
      const rawStatus = item.status || ''
      const normalizedStatus =
        rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
      const isActiveStatus = normalizedStatus === ACTIVE
      return compatible && isActiveStatus
    }

    return true
  })

  return comparator ? filteredList.sort(comparator) : filteredList
}
